import { watch } from 'vue';
import type { PiniaPluginContext, StateTree } from 'pinia';

/**
 * Per-store persistence configuration, mirroring the shape of the legacy `vuex-persist`
 * `VuexPersistence` options, but scoped to a single Pinia store instead of the whole tree.
 *
 * Both storages can be enabled at once (see `livechat`, which persists to both); on hydration
 * sessionStorage is applied last, so it wins over localStorage on overlapping keys — this
 * mirrors `plugins: [vuexLocal.plugin, vuexSession.plugin]` order in the legacy `src/store/index.ts`
 * (vuexSession's restore runs after vuexLocal's, so it's the last `replaceState` call).
 */
export interface VuexPersistOptions {
  /** Key inside the single `vuex` storage item. Defaults to the store's `$id`. */
  vuexKey?: string;
  /** Persist the full state (`true`) or only these top-level state props. */
  local?: boolean | string[];
  /** Persist the full state (`true`) or only these top-level state props. */
  session?: boolean | string[];
}

declare module 'pinia' {
   
  export interface DefineStoreOptionsBase<S extends StateTree, Store> {
    /**
     * Opt this store into the vuex-persist-compat plugin. See
     * `src/stores/plugins/vuexPersistCompat.ts` for the full behavior. Omit entirely for
     * in-memory-only stores (the vast majority).
     */
    vuexPersist?: VuexPersistOptions;
  }
}

/** The single storage-item name `vuex-persist` (and this plugin) reads/writes in both storages. */
const VUEX_STORAGE_ITEM_KEY = 'vuex';

/** Resolved, defaulted form of `VuexPersistOptions` (a store's `$id` filled in for `vuexKey`). */
interface ResolvedVuexPersistConfig {
  vuexKey: string;
  local: boolean | string[] | undefined;
  session: boolean | string[] | undefined;
}

/**
 * Resolves a store's raw `vuexPersist` option (defaulting `vuexKey` to the store id). Exported
 * because it's handy for tests/tooling that want to know which storage item key a given store
 * reads/writes without duplicating the default-resolution logic.
 */
export function getVuexPersistConfig(
  storeId: string,
  options: VuexPersistOptions,
): ResolvedVuexPersistConfig {
  return {
    vuexKey: options.vuexKey ?? storeId,
    local: options.local,
    session: options.session,
  };
}

/** JSON.parse with try/catch, returning `null` for anything that isn't a valid plain object. */
function safeParseStorageItem(raw: string | null): Record<string, any> | null {
  if (typeof raw !== 'string') return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Applies the `pick` subset (matching vuex-persist's `modules`/reducer-based partial persistence)
 * to a slice of state. `pick === true` (or `undefined`, meaning "everything") returns the slice
 * unchanged; `pick === false` returns `undefined` (nothing to persist/restore); an array picks
 * only those top-level props.
 */
function applyPick(
  slice: Record<string, any>,
  pick: boolean | string[] | undefined,
): Record<string, any> | undefined {
  if (pick === false || pick === undefined) return undefined;
  if (pick === true) return slice;

  const picked: Record<string, any> = {};
  for (const key of pick) {
    if (Object.prototype.hasOwnProperty.call(slice, key)) {
      picked[key] = slice[key];
    }
  }
  return picked;
}

/**
 * Hydrates `store` from one storage's `vuex` item, applying `pick` and merging via `$patch`.
 *
 * Array handling: vuex-persist's default `mergeOption` is `'replaceArrays'`
 * (`arrayMerge: (destinationArray, sourceArray) => sourceArray`, see
 * `node_modules/vuex-persist/dist/esm/index.js`) — i.e. arrays from the persisted slice
 * *replace* the corresponding array in the live state, they are never concatenated. Pinia's
 * `$patch(partialState)` has the same replace-not-concat semantics for arrays (only plain
 * objects are merged key-by-key; arrays and other values are assigned wholesale), so using
 * `$patch` here reproduces vuex-persist's array behavior exactly with no extra work.
 */
function hydrateFromStorage(
  store: { $id: string; $patch: (partial: any) => void },
  storage: Storage,
  pick: boolean | string[] | undefined,
  vuexKey: string,
): void {
  if (pick === false || pick === undefined) return;

  try {
    const item = safeParseStorageItem(storage.getItem(VUEX_STORAGE_ITEM_KEY));
    if (!item || !Object.prototype.hasOwnProperty.call(item, vuexKey)) return;

    const slice = item[vuexKey];
    if (slice === null || typeof slice !== 'object') return;

    const picked = applyPick(slice, pick);
    if (picked && Object.keys(picked).length > 0) {
      store.$patch(picked);
    }
  } catch (e) {
    console.error(`[vuexPersistCompat] Failed to hydrate store "${store.$id}" from storage`, e);
  }
}

/**
 * Read-modify-write persists `state` (after `pick`) under `item[vuexKey]` in `storage`'s single
 * `vuex` item, preserving every other key in that item verbatim (they may be owned by other
 * Pinia stores, or written by the old hub through the iframe-sync bridge).
 *
 * Note this differs from legacy `vuex-persist`, which does NOT read-modify-write: a single
 * `VuexPersistence` instance owns one global multi-namespace `reducer` covering the whole vuex
 * tree, so it can safely `JSON.stringify` the full reduced object on every mutation without
 * clobbering anything. Pinia has one plugin invocation *per store*, so each store's write must
 * merge into the shared item instead of overwriting it, per the design doc.
 */
function writeToStorage(
  state: StateTree,
  storage: Storage,
  pick: boolean | string[] | undefined,
  vuexKey: string,
  storeId: string,
): void {
  if (pick === false || pick === undefined) return;

  try {
    // Pick first, so only the persisted keys get serialized (not e.g. the whole playground history)
    const picked = applyPick(state, pick);
    if (picked === undefined) return;

    const item = safeParseStorageItem(storage.getItem(VUEX_STORAGE_ITEM_KEY)) ?? {};
    item[vuexKey] = picked;
    storage.setItem(VUEX_STORAGE_ITEM_KEY, JSON.stringify(item));
  } catch (e) {
    // Resilient to storage quota errors (and any other write failure) — never throw out of a
    // $subscribe callback, and never lose the in-memory state over a persistence hiccup.
    console.error(`[vuexPersistCompat] Failed to persist store "${storeId}" to storage`, e);
  }
}

/**
 * Pinia plugin replicating `vuex-persist` behavior against the same `vuex` storage items in
 * `window.localStorage` / `window.sessionStorage`. Opt in per-store via the `vuexPersist` store
 * option (see `VuexPersistOptions`); stores without it are left completely untouched (in-memory
 * only, matching every non-persisted legacy vuex module).
 */
export default function vuexPersistCompat(context: PiniaPluginContext): void {
  const { store, options } = context;
  const config = options.vuexPersist;
  if (!config) return;

  const { vuexKey, local, session } = getVuexPersistConfig(store.$id, config);

  // Hydration: localStorage slice first, then sessionStorage slice OVER it (session wins on
  // overlap), matching `plugins: [vuexLocal.plugin, vuexSession.plugin]` restore order.
  hydrateFromStorage(store, window.localStorage, local, vuexKey);
  hydrateFromStorage(store, window.sessionStorage, session, vuexKey);

  // Write-back: synchronous (no debounce — vuex-persist wrote synchronously per mutation, and
  // draft-like data must survive a tab close).
  //
  // Deliberately NOT `store.$subscribe`: that deep-watches the WHOLE store state, so a store that
  // persists only a subset (knowledgeBase: drafts only) still walked its entire non-persisted
  // cache — tens of thousands of resources — synchronously on every nested write. Filling that
  // cache one resource at a time froze the tab (#2595). Watch only what actually gets persisted.
  //
  // Plugins run inside the store's own effect scope, so this watcher lives as long as the store,
  // not as long as whatever component happened to use the store first.
  const persistedKeys = [...new Set([local, session].flatMap((pick) => (Array.isArray(pick) ? pick : [])))];
  const persistsFullState = local === true || session === true;

  watch(
    () => (persistsFullState ? store.$state : persistedKeys.map((key) => store.$state[key])),
    () => {
      if (local) writeToStorage(store.$state, window.localStorage, local, vuexKey, store.$id);
      if (session) writeToStorage(store.$state, window.sessionStorage, session, vuexKey, store.$id);
    },
    { deep: true, flush: 'sync' },
  );
}
